import { ErrorBoundary } from '@sentry/react';
import React from 'react';
import { hot } from 'react-hot-loader';
import { Redirect, Route, Switch } from 'react-router';
import App from './components/App';
import CampaignDetail from './components/organisms/campaign/CampaignDetail';
import MoneyAccountHookDetail from './components/organisms/campaign/MoneyAccountHookDetail';
import AllMobilePushOperations from './components/pages/AllMobilePushOperations';
import Bills from './components/pages/Bills';
import Checks from './components/pages/Checks';
import ClearingDetail from './components/pages/ClearingDetail';
import Clearings from './components/pages/Clearings';
import ClearingExportBatchDetail from './components/pages/ClearingExportBatchDetail';
import ClearingExportBatches from './components/pages/ClearingExportBatches';
import CouponDetail from './components/pages/CouponDetail';
import CouponList from './components/pages/CouponList';
import CreateBills from './components/pages/CreateBills';
import CreateChecks from './components/pages/CreateChecks';
import CreateDevice from './components/pages/CreateDevice';
import CreateMemberOrganization from './components/pages/CreateMemberOrganization';
import CreateMerchantApplicationTemplate from './components/pages/CreateMerchantApplicationTemplate';
import CreatePrivateMoney from './components/pages/CreatePrivateMoney';
import CreatePrivateMoneyExchange from './components/pages/CreatePrivateMoneyExchange';
import CreateShop from './components/pages/CreateShop';
import CreateSoftware from './components/pages/CreateSoftware';
import CreateSoftwareRelease from './components/pages/CreateSoftwareRelease';
import CustomerDetail from './components/pages/CustomerDetail';
import CustomerDetailDebt from './components/pages/CustomerDetailDebt';
import Customers from './components/pages/Customers';
import DeviceKiosk from './components/pages/Device/Kiosk';
import DevicePaybox from './components/pages/Device/Paybox';
import DevicePayregi from './components/pages/Device/Payregi';
import DevicePayspot from './components/pages/Device/Payspot';
import DevicePokeregi from './components/pages/Device/Pokeregi';
import Home from './components/pages/Home';
import InviteUser from './components/pages/InviteUser';
import Login from './components/pages/Login';
import MerchantApplicationDetail from './components/pages/MerchantApplicationDetail';
import MerchantApplicationFormApply from './components/pages/MerchantApplicationFormApply';
import MerchantApplicationFormPreview from './components/pages/MerchantApplicationFormPreview';
import MerchantApplications from './components/pages/MerchantApplications';
import MerchantApplicationTemplateDetails from './components/pages/MerchantApplicationTemplateDetails';
import MerchantApplicationTemplates from './components/pages/MerchantApplicationTemplates';
import MobilePushOperationDetail from './components/pages/MobilePushOperationDetail';
import MobilePushOperationEditForm from './components/pages/MobilePushOperationEditForm';
import MobilePushOperationNewForm from './components/pages/MobilePushOperationNewForm';
import MobilePushOperations from './components/pages/MobilePushOperations';
import OAuthClients from './components/pages/OAuthClients';
import CreateOAuthClient from './components/pages/OAuthClients/CreateOAuthClient';
import OAuthClientDetail from './components/pages/OAuthClients/OAuthClientDetail';
import PartnerClients from './components/pages/PartnerClients';
import CreatePartnerClient from './components/pages/PartnerClients/CreatePartnerClient';
import PartnerClientDetail from './components/pages/PartnerClients/PartnerClientDetail';
import PasswordReset from './components/pages/PasswordReset';
import PrivateMoneyExchanges from './components/pages/PrivateMoneyExchanges';
import Promotion from './components/pages/Promotion';
import QRDesigner from './components/pages/QRDesigner';
import QRDisplay from './components/pages/QRDesigner/QRDisplay';
import Rewards from './components/pages/Rewards';
import RewardsRegister from './components/pages/RewardsRegister';
import SelfOrganizationDetail from './components/pages/SelfOrganizationDetail';
import ServiceSettingsAccessToken from './components/pages/ServiceSettingsAccessToken';
import SettingsCampaigns from './components/pages/SettingsCampaigns';
import SettingsDevice from './components/pages/SettingsDevice';
import DeviceKioskCashCollection from './components/pages/SettingsDevice/KioskCashCollection';
import DeviceKioskHealth from './components/pages/SettingsDevice/KioskHealth';
import DeviceKioskMaintenance from './components/pages/SettingsDevice/KioskMaintenance';
import DeviceKioskPlays from './components/pages/SettingsDevice/KioskPlays';
import DeviceKioskPlayDetail from './components/pages/SettingsDevice/KioskPlays/KioskPlayDetail';
import SettingsMemberOrganization from './components/pages/SettingsMemberOrganization';
import SettingsMoney from './components/pages/SettingsMoney';
import SettingsShop from './components/pages/SettingsShop';
import SettingsUser from './components/pages/SettingsUser';
import Shop from './components/pages/Shop';
import ShopApp from './components/pages/ShopApp';
import ShopApplicationDetails from './components/pages/ShopApplicationDetails';
import ShopApplicationFormRequestChange from './components/pages/ShopApplicationFormRequestChange';
import ShopApplicationFormRequestNew from './components/pages/ShopApplicationFormRequestNew';
import ShopApplications from './components/pages/ShopApplications';
import ShopQRDesign from './components/pages/ShopQRDesign';
import SoftwareReleaseDetail from './components/pages/SoftwareReleaseDetail';
import SoftwareReleases from './components/pages/SoftwareReleases';
import Softwares from './components/pages/Softwares';
import TransactionDetail from './components/pages/TransactionDetail';
import Transactions from './components/pages/Transactions';
import UpdateDevicePokeregi from './components/pages/UpdateDevice/Pokeregi';
import UpdateMemberOrganization from './components/pages/UpdateMemberOrganization';
import UpdateShop from './components/pages/UpdateShop';
import UpdateUser from './components/pages/UpdateUser';
import VerifyUser from './components/pages/VerifyUser';
import Authorize from './components/shared/Authorize';
import NotFound from './components/shared/NotFound';
import OnePane from './components/templates/OnePane';
import WithHeaderSidebar from './components/templates/WithHeaderSidebar';

import CreateUserAttributeSchema from './components/pages/CreateUserAttributeSchema';
import SettingsCustomer from './components/pages/SettingsCustomer';

import {
  allAllowed,
  allIssuerAllowed,
  canCreateDevice,
  canCreateShop,
  canInvalidatePokeregi,
  canManageThirdPartyIssuerOrg,
  canManageThirdPartyShopApplications,
  canShowDevice,
  canShowShop,
  managerAllowed,
  nonServiceRoles,
  nonShopRoles,
  organizationAllowed,
  organizationManagerAllowed,
  organizationManagerOnlyAllowed,
  refundAllowed,
  requireMerchantApplication,
  serviceAllowed,
  serviceAndOrgManagerAllowed,
  serviceManagerAllowed,
  shopRoles,
  thirdPartyIssuerAllowed,
} from './authorities';
import PageCrashErrorAlert from './components/atoms/PageCrashErrorAlert';
import BillsDetail from './components/pages/BillsDetails';
import BulkCreateChecks from './components/pages/BulkCreateChecks';
import Campaigns from './components/pages/Campaigns';
import ChecksDetails from './components/pages/ChecksDetails';
import Clearing from './components/pages/Clearing';
import ClearingAccount from './components/pages/ClearingAccount';
import ClearingAutomatedSetting from './components/pages/ClearingAutomatedSetting';
import ClearingSummary from './components/pages/ClearingSummary';
import ConvenienceStoreRequests from './components/pages/ConvenienceStoreRequests';
import CreateDeviceKeys from './components/pages/CreateDeviceKeys';
import CustomerDetailBalance from './components/pages/CustomerDetailBalance';
import DeviceKeys from './components/pages/DeviceKeys';
import DeviceKeysDetail from './components/pages/DeviceKeysDetail';
import EditIssuerOrganization from './components/pages/EditIssuerOrganization';
import IssuerOrganizations from './components/pages/IssuerOrganizations';
import MyNumberAuthRequests from './components/pages/MyNumberAuthRequests';
import RenamePrivateMoney from './components/pages/RenamePrivateMoney';
import TransactionsC2C from './components/pages/TransactionsC2C';
import TransactionsExpired from './components/pages/TransactionsExpired';
import { useAppSelector } from './hooks/useApp';
import { selectUserPrivateMoney } from './reducers/user-private-money';
import SettingsShopLabel from 'admin/components/pages/SettingsShopLabel';
import SettingsShopLabelDetail from 'admin/components/pages/SettingsShopLabel/Detail';
import System from 'admin/components/pages/System';

// XXX: should directly import from authorities.js
export {
  allAllowed,
  allIssuerAllowed,
  canCreateDevice,
  canCreateShop,
  canInvalidatePokeregi,
  canShowDevice,
  canShowShop,
  managerAllowed,
  nonServiceRoles,
  nonShopRoles,
  organizationAllowed,
  organizationManagerAllowed,
  refundAllowed,
  serviceAllowed,
  shopRoles,
  thirdPartyIssuerAllowed,
};

const AppRoute = ({ component: Component, layout: Layout, ...rest }) => (
  <Route
    {...rest}
    render={(props) => (
      <Layout>
        <ErrorBoundary fallback={<PageCrashErrorAlert />}>
          <Component {...props} />
        </ErrorBoundary>
      </Layout>
    )}
  />
);

// isUserAllowedFn is for viewing the page (if not allowed, it shows a Not Found 404)
// isPathAllowedFn is strictly for the display of the link on the Sidebar
const secureRoutes = [
  {
    path: '/',
    exact: true,
    allowedRoles: allAllowed,
    layout: WithHeaderSidebar,
    component: Home,
  },
  {
    path: '/transactions',
    exact: true,
    allowedRoles: allAllowed,
    layout: WithHeaderSidebar,
    component: Transactions,
  },
  {
    path: '/clearing/transactions',
    exact: true,
    allowedRoles: allAllowed,
    layout: WithHeaderSidebar,
    component: Transactions,
  },
  {
    path: '/transactions/expired',
    exact: true,
    allowedRoles: organizationAllowed,
    isPathAllowedFn: ({ user, selectedPrivateMoney }) =>
      user.isServiceUser?.() ||
      (user.isIssuerOf?.(selectedPrivateMoney?.id) && !user.isShopUser?.()),
    layout: WithHeaderSidebar,
    component: TransactionsExpired,
  },
  {
    path: '/transactions/transfer',
    exact: true,
    allowedRoles: organizationAllowed,
    layout: WithHeaderSidebar,
    component: TransactionsC2C,
  },
  {
    path: '/transactions/:id',
    exact: false,
    allowedRoles: allAllowed,
    layout: WithHeaderSidebar,
    component: TransactionDetail,
  },
  {
    path: '/cvs-request',
    exact: true,
    allowedRoles: organizationManagerAllowed,
    layout: WithHeaderSidebar,
    component: ConvenienceStoreRequests,
  },
  {
    path: '/itrust-request',
    exact: true,
    allowedRoles: organizationManagerAllowed,
    isUserAllowedFn: ({ user }) =>
      user.isServiceUser?.() ||
      (organizationManagerAllowed.includes(user.role) &&
        user.privateMoneys.some((money) => money.organization.code === user.organization.code)),
    layout: WithHeaderSidebar,
    component: MyNumberAuthRequests,
  },
  {
    path: '/clearing/summary',
    exact: true,
    allowedRoles: organizationManagerAllowed,
    layout: WithHeaderSidebar,
    component: ClearingSummary,
  },
  {
    path: '/clearings',
    exact: true,
    allowedRoles: organizationManagerAllowed,
    layout: WithHeaderSidebar,
    component: Clearings,
  },
  {
    path: '/clearings/export-batches',
    exact: true,
    allowedRoles: organizationManagerAllowed,
    issuerType: thirdPartyIssuerAllowed,
    layout: WithHeaderSidebar,
    component: ClearingExportBatches,
  },
  {
    path: '/clearings/export-batches/:id',
    exact: true,
    allowedRoles: organizationManagerAllowed,
    issuerType: thirdPartyIssuerAllowed,
    layout: WithHeaderSidebar,
    component: ClearingExportBatchDetail,
  },
  {
    path: '/clearing/account',
    exact: true,
    allowedRoles: organizationManagerAllowed,
    isUserAllowedFn: ({ user, selectedPrivateMoney }) =>
      user.isServiceUser?.() ||
      (organizationManagerAllowed.includes(user.role) &&
        user.isIssuerOf?.(selectedPrivateMoney?.id)),
    layout: WithHeaderSidebar,
    component: ClearingAccount,
  },
  {
    path: '/clearing',
    exact: true,
    allowedRoles: organizationManagerAllowed,
    issuerType: thirdPartyIssuerAllowed,
    layout: WithHeaderSidebar,
    component: Clearing,
  },
  {
    path: '/clearing/automation',
    exact: true,
    allowedRoles: organizationManagerAllowed,
    issuerType: thirdPartyIssuerAllowed,
    layout: WithHeaderSidebar,
    component: ClearingAutomatedSetting,
  },
  {
    path: '/private_money/:moneyId/clearings/:id',
    exact: true,
    allowedRoles: organizationManagerAllowed,
    layout: WithHeaderSidebar,
    component: ClearingDetail,
  },
  {
    path: '/promotion',
    exact: true,
    allowedRoles: allAllowed,
    layout: WithHeaderSidebar,
    component: Promotion,
  },
  {
    path: '/promotion/campaigns',
    exact: true,
    issuerType: allIssuerAllowed,
    allowedRoles: organizationAllowed,
    layout: WithHeaderSidebar,
    component: Campaigns,
  },
  {
    path: '/promotion/campaigns/:id',
    exact: true,
    allowedRoles: organizationAllowed,
    issuerType: allIssuerAllowed,
    layout: WithHeaderSidebar,
    component: CampaignDetail,
  },
  {
    path: '/promotion/private-moneys/:id/money-account-hooks/:id',
    exact: true,
    allowedRoles: organizationAllowed,
    issuerType: allIssuerAllowed,
    layout: WithHeaderSidebar,
    component: MoneyAccountHookDetail,
  },
  {
    path: '/promotion/rewards/create',
    exact: true,
    issuerType: allIssuerAllowed,
    allowedRoles: organizationManagerAllowed,
    layout: WithHeaderSidebar,
    component: RewardsRegister,
  },
  {
    path: '/promotion/rewards',
    exact: true,
    issuerType: allIssuerAllowed,
    allowedRoles: organizationManagerAllowed,
    layout: WithHeaderSidebar,
    component: Rewards,
  },
  {
    path: '/promotion/mobile-push-operations',
    exact: true,
    issuerType: allIssuerAllowed,
    allowedRoles: organizationManagerOnlyAllowed,
    layout: WithHeaderSidebar,
    component: MobilePushOperations,
  },
  {
    path: '/promotion/mobile-push-operations/create',
    exact: true,
    issuerType: allIssuerAllowed,
    allowedRoles: organizationManagerOnlyAllowed,
    layout: WithHeaderSidebar,
    component: MobilePushOperationNewForm,
  },
  {
    path: '/promotion/mobile-push-operations/:id/edit',
    exact: false,
    issuerType: allIssuerAllowed,
    allowedRoles: organizationManagerOnlyAllowed,
    layout: WithHeaderSidebar,
    component: MobilePushOperationEditForm,
  },
  {
    path: '/promotion/mobile-push-operations/:id',
    exact: false,
    issuerType: allIssuerAllowed,
    allowedRoles: organizationManagerAllowed,
    layout: WithHeaderSidebar,
    component: MobilePushOperationDetail,
  },
  {
    path: '/promotion/bills/create',
    exact: true,
    allowedRoles: allAllowed,
    layout: WithHeaderSidebar,
    component: CreateBills,
  },
  {
    path: '/promotion/bills',
    exact: true,
    allowedRoles: allAllowed,
    layout: WithHeaderSidebar,
    component: Bills,
  },
  {
    path: '/promotion/bills/:id',
    exact: true,
    allowedRoles: allAllowed,
    layout: WithHeaderSidebar,
    component: BillsDetail,
  },
  {
    path: '/promotion/checks',
    exact: true,
    isUserAllowedFn: ({ user }) => user.isAllowedTo('read', 'check'),
    layout: WithHeaderSidebar,
    component: Checks,
  },
  {
    path: '/promotion/checks/create',
    exact: true,
    isUserAllowedFn: ({ user }) => user.isAllowedTo('write', 'check'),
    layout: WithHeaderSidebar,
    component: CreateChecks,
  },
  {
    path: '/promotion/checks/bulk-create',
    exact: true,
    isUserAllowedFn: ({ user }) => user.isAllowedTo('write', 'check'),
    layout: WithHeaderSidebar,
    component: BulkCreateChecks,
  },
  {
    path: '/promotion/checks/:id',
    exact: true,
    isUserAllowedFn: ({ user }) => user.isAllowedTo('read', 'check'),
    layout: WithHeaderSidebar,
    component: ChecksDetails,
  },
  {
    path: '/promotion/shop-qr-design',
    exact: true,
    allowedRoles: allAllowed,
    layout: WithHeaderSidebar,
    component: ShopQRDesign,
  },
  {
    path: '/promotion/coupons',
    exact: true,
    allowedRoles: organizationManagerAllowed,
    layout: WithHeaderSidebar,
    component: CouponList,
  },
  {
    path: '/promotion/coupons/:id',
    exact: true,
    allowedRoles: organizationManagerAllowed,
    layout: WithHeaderSidebar,
    component: CouponDetail,
  },
  {
    path: '/customers',
    exact: true,
    issuerType: allIssuerAllowed,
    allowedRoles: organizationAllowed,
    layout: WithHeaderSidebar,
    component: Customers,
  },
  {
    path: '/customers/:id/debt',
    exact: false,
    issuerType: allIssuerAllowed,
    allowedRoles: organizationAllowed,
    layout: WithHeaderSidebar,
    component: CustomerDetailDebt,
  },
  {
    path: '/customers/:id/balance',
    exact: false,
    issuerType: allIssuerAllowed,
    allowedRoles: organizationAllowed,
    layout: WithHeaderSidebar,
    component: CustomerDetailBalance,
  },
  {
    path: '/customers/:id',
    exact: false,
    issuerType: allIssuerAllowed,
    allowedRoles: organizationAllowed,
    layout: WithHeaderSidebar,
    component: CustomerDetail,
  },
  {
    path: '/settings/admin-users',
    exact: true,
    allowedRoles: allAllowed,
    layout: WithHeaderSidebar,
    component: SettingsUser,
  },
  {
    path: '/settings/admin-users/invite',
    exact: true,
    allowedRoles: organizationManagerAllowed,
    layout: WithHeaderSidebar,
    component: InviteUser,
  },
  {
    path: '/settings/admin-users/:id/edit',
    exact: false,
    allowedRoles: nonShopRoles,
    layout: WithHeaderSidebar,
    component: UpdateUser,
  },
  {
    path: '/settings/private-money',
    exact: true,
    issuerType: allIssuerAllowed,
    allowedRoles: allAllowed,
    layout: WithHeaderSidebar,
    component: SettingsMoney,
  },
  {
    path: '/settings/private-money-exchange',
    exact: true,
    issuerType: allIssuerAllowed,
    allowedRoles: allAllowed,
    layout: WithHeaderSidebar,
    component: PrivateMoneyExchanges,
  },
  {
    path: '/settings/private-money-exchange/create',
    exact: true,
    issuerType: allIssuerAllowed,
    allowedRoles: organizationManagerAllowed,
    layout: WithHeaderSidebar,
    component: CreatePrivateMoneyExchange,
  },
  {
    path: '/organizations/application-form',
    exact: true,
    issuerType: allIssuerAllowed,
    allowedRoles: organizationAllowed,
    isUserAllowedFn: requireMerchantApplication(canManageThirdPartyIssuerOrg),
    layout: WithHeaderSidebar,
    component: MerchantApplicationTemplates,
  },
  {
    path: '/organizations/application-form/create',
    exact: true,
    issuerType: allIssuerAllowed,
    allowedRoles: organizationAllowed,
    isUserAllowedFn: requireMerchantApplication(canManageThirdPartyIssuerOrg),
    layout: WithHeaderSidebar,
    component: CreateMerchantApplicationTemplate,
  },
  {
    path: '/organizations/application-form/:id',
    exact: true,
    issuerType: allIssuerAllowed,
    allowedRoles: organizationAllowed,
    isUserAllowedFn: requireMerchantApplication(canManageThirdPartyIssuerOrg),
    layout: WithHeaderSidebar,
    component: MerchantApplicationTemplateDetails,
  },
  {
    path: '/organizations/application',
    exact: true,
    issuerType: allIssuerAllowed,
    allowedRoles: organizationAllowed,
    isUserAllowedFn: requireMerchantApplication(canManageThirdPartyIssuerOrg),
    layout: WithHeaderSidebar,
    component: MerchantApplications,
  },
  {
    path: '/organizations/application/:id',
    exact: true,
    issuerType: allIssuerAllowed,
    allowedRoles: organizationAllowed,
    isUserAllowedFn: requireMerchantApplication(canManageThirdPartyIssuerOrg),
    layout: WithHeaderSidebar,
    component: MerchantApplicationDetail,
  },
  {
    path: '/settings/self-organization-detail',
    exact: true,
    allowedRoles: organizationAllowed,
    isUserAllowedFn: ({ user, selectedPrivateMoney }) =>
      user.isOrgUser?.() && !user.isIssuerOf?.(selectedPrivateMoney?.id),
    layout: WithHeaderSidebar,
    component: SelfOrganizationDetail,
  },
  {
    path: '/shops',
    exact: true,
    allowedRoles: allAllowed,
    layout: WithHeaderSidebar,
    component: SettingsShop,
  },
  {
    path: '/shops/create',
    exact: true,
    allowedRoles: organizationAllowed,
    isUserAllowedFn: ({ user, selectedPrivateMoney }) =>
      user.isServiceUser?.() ||
      (user.isOrgUser?.() &&
        !!selectedPrivateMoney &&
        user.isIssuerOfMoney?.(selectedPrivateMoney)),
    layout: WithHeaderSidebar,
    component: CreateShop,
  },
  {
    path: '/shops/application-create',
    exact: true,
    allowedRoles: allAllowed,
    isUserAllowedFn: requireMerchantApplication(
      ({ user, selectedPrivateMoney }) =>
        !user.isShopUser?.() &&
        !!selectedPrivateMoney &&
        !user.isIssuerOfMoney?.(selectedPrivateMoney)
    ),
    layout: WithHeaderSidebar,
    component: ShopApplicationFormRequestNew,
  },
  {
    path: '/shops/applications',
    exact: true,
    allowedRoles: organizationAllowed,
    isUserAllowedFn: requireMerchantApplication(canManageThirdPartyShopApplications),
    layout: WithHeaderSidebar,
    component: ShopApplications,
  },
  {
    path: '/shops/applications/:id',
    exact: true,
    allowedRoles: organizationAllowed,
    isUserAllowedFn: requireMerchantApplication(canManageThirdPartyShopApplications),
    layout: WithHeaderSidebar,
    component: ShopApplicationDetails,
  },
  {
    path: '/shops/:shopId/application',
    exact: true,
    allowedRoles: allAllowed,
    isUserAllowedFn: requireMerchantApplication(
      ({ user, selectedPrivateMoney }) =>
        !!selectedPrivateMoney && !user.isIssuerOfMoney?.(selectedPrivateMoney)
    ),
    layout: WithHeaderSidebar,
    component: ShopApplicationFormRequestChange,
  },
  {
    path: '/shops/:id/edit',
    exact: false,
    allowedRoles: canCreateShop,
    layout: WithHeaderSidebar,
    component: UpdateShop,
  },
  {
    path: '/shops/:id/app',
    exact: true,
    allowedRoles: canCreateShop,
    layout: WithHeaderSidebar,
    component: ShopApp,
  },
  {
    path: '/shops/:id',
    exact: false,
    allowedRoles: canShowShop,
    layout: WithHeaderSidebar,
    component: Shop,
  },
  {
    path: '/settings/devices',
    exact: true,
    allowedRoles: allAllowed,
    layout: WithHeaderSidebar,
    component: SettingsDevice,
  },
  {
    path: '/settings/devices/create',
    exact: true,
    allowedRoles: serviceAllowed,
    layout: WithHeaderSidebar,
    component: CreateDevice,
  },
  {
    path: '/settings/devices/pokeregi/:id/edit',
    exact: false,
    allowedRoles: canCreateDevice,
    layout: WithHeaderSidebar,
    component: UpdateDevicePokeregi,
  },
  {
    path: '/settings/devices/pokeregi/:id',
    exact: false,
    allowedRoles: canShowDevice,
    layout: WithHeaderSidebar,
    component: DevicePokeregi,
  },
  {
    path: '/settings/devices/payregi/:id',
    exact: false,
    allowedRoles: canShowDevice,
    layout: WithHeaderSidebar,
    component: DevicePayregi,
  },
  {
    path: '/settings/devices/paybox/:id',
    exact: false,
    allowedRoles: canShowDevice,
    layout: WithHeaderSidebar,
    component: DevicePaybox,
  },
  {
    path: '/settings/devices/payspot/:id',
    exact: false,
    allowedRoles: canShowDevice,
    layout: WithHeaderSidebar,
    component: DevicePayspot,
  },
  {
    path: '/settings/devices/kiosk/maintenances',
    exact: false,
    allowedRoles: canShowDevice,
    layout: WithHeaderSidebar,
    component: DeviceKioskMaintenance,
  },
  {
    path: '/settings/devices/kiosk/health',
    exact: false,
    allowedRoles: canShowDevice,
    layout: WithHeaderSidebar,
    component: DeviceKioskHealth,
  },
  {
    path: '/settings/devices/kiosk/plays',
    exact: false,
    allowedRoles: canShowDevice,
    layout: WithHeaderSidebar,
    component: DeviceKioskPlays,
  },
  {
    path: '/settings/devices/kiosks/plays/:id',
    exact: false,
    allowedRoles: canShowDevice,
    layout: WithHeaderSidebar,
    component: DeviceKioskPlayDetail,
  },
  {
    path: '/settings/devices/kiosk/cash-collect',
    exact: false,
    allowedRoles: canShowDevice,
    layout: WithHeaderSidebar,
    component: DeviceKioskCashCollection,
  },
  {
    path: '/settings/devices/kiosk/:id',
    exact: false,
    allowedRoles: canShowDevice,
    layout: WithHeaderSidebar,
    component: DeviceKiosk,
  },
  {
    path: '/settings/device-keys',
    exact: true,
    allowedRoles: organizationManagerAllowed,
    layout: WithHeaderSidebar,
    component: DeviceKeys,
  },
  {
    path: '/settings/device-keys/create',
    exact: true,
    isUserAllowedFn: ({ user, selectedPrivateMoney }) =>
      organizationManagerAllowed.includes(user.role) &&
      (selectedPrivateMoney?.organization?.code === 'pocketchange' ||
        selectedPrivateMoney?.organization?.code === user.organization?.code),
    layout: WithHeaderSidebar,
    component: CreateDeviceKeys,
  },
  {
    path: '/settings/device-keys/:id',
    exact: false,
    allowedRoles: organizationManagerAllowed,
    layout: WithHeaderSidebar,
    component: DeviceKeysDetail,
  },
  {
    path: '/organizations/create',
    exact: true,
    issuerType: thirdPartyIssuerAllowed,
    allowedRoles: organizationAllowed,
    isUserAllowedFn: canManageThirdPartyIssuerOrg,
    layout: WithHeaderSidebar,
    component: CreateMemberOrganization,
  },
  {
    path: '/organizations/:id',
    exact: true,
    allowedRoles: organizationAllowed,
    isUserAllowedFn: canManageThirdPartyIssuerOrg,
    layout: WithHeaderSidebar,
    component: UpdateMemberOrganization,
  },
  {
    path: '/organizations',
    exact: true,
    issuerType: thirdPartyIssuerAllowed,
    allowedRoles: organizationAllowed,
    isUserAllowedFn: canManageThirdPartyIssuerOrg,
    layout: WithHeaderSidebar,
    component: SettingsMemberOrganization,
  },
  {
    path: '/settings/private-money/:id/organizations',
    exact: true,
    issuerType: thirdPartyIssuerAllowed,
    allowedRoles: organizationAllowed,
    layout: WithHeaderSidebar,
    component: SettingsMemberOrganization,
  },
  {
    path: '/service-settings/user-attribute-schema/create',
    exact: true,
    allowedRoles: serviceAllowed,
    layout: WithHeaderSidebar,
    component: CreateUserAttributeSchema,
  },
  {
    path: '/service-settings/private-money/rename',
    exact: true,
    allowedRoles: serviceAllowed,
    layout: WithHeaderSidebar,
    component: RenamePrivateMoney,
  },
  {
    path: '/service-settings/private-money/create',
    exact: true,
    allowedRoles: serviceAllowed,
    layout: WithHeaderSidebar,
    component: CreatePrivateMoney,
  },
  {
    path: '/service-settings/issuer-organization',
    exact: true,
    allowedRoles: serviceAllowed,
    layout: WithHeaderSidebar,
    component: IssuerOrganizations,
  },
  {
    path: '/service-settings/issuer-organization/create',
    exact: true,
    allowedRoles: serviceAllowed,
    layout: WithHeaderSidebar,
    component: EditIssuerOrganization,
  },
  {
    path: '/service-settings/issuer-organization/:id',
    exact: true,
    allowedRoles: serviceAllowed,
    layout: WithHeaderSidebar,
    component: EditIssuerOrganization,
  },
  {
    path: '/service-settings/access-token',
    exact: true,
    allowedRoles: serviceAllowed,
    layout: WithHeaderSidebar,
    component: ServiceSettingsAccessToken,
  },
  {
    path: '/service-settings/software/create',
    exact: true,
    allowedRoles: serviceAllowed,
    layout: WithHeaderSidebar,
    component: CreateSoftware,
  },
  {
    path: '/service-settings/software',
    exact: true,
    allowedRoles: serviceAllowed,
    layout: WithHeaderSidebar,
    component: Softwares,
  },
  {
    path: '/service-settings/software-release/create',
    exact: true,
    allowedRoles: serviceAllowed,
    layout: WithHeaderSidebar,
    component: CreateSoftwareRelease,
  },
  {
    path: '/service-settings/software-release/:id',
    exact: true,
    allowedRoles: serviceAllowed,
    layout: WithHeaderSidebar,
    component: SoftwareReleaseDetail,
  },
  {
    path: '/service-settings/software-release',
    exact: true,
    allowedRoles: serviceAllowed,
    layout: WithHeaderSidebar,
    component: SoftwareReleases,
  },
  {
    path: '/service-settings/all-mobile-push-operations',
    exact: true,
    issuerType: allIssuerAllowed,
    allowedRoles: serviceManagerAllowed,
    layout: WithHeaderSidebar,
    component: AllMobilePushOperations,
  },
  {
    path: '/settings/system',
    exact: true,
    issuerType: allIssuerAllowed,
    allowedRoles: managerAllowed,
    layout: WithHeaderSidebar,
    component: System,
  },
  {
    path: '/settings/shop-label',
    exact: true,
    issuerType: allIssuerAllowed,
    allowedRoles: serviceAndOrgManagerAllowed,
    layout: WithHeaderSidebar,
    component: SettingsShopLabel,
  },
  {
    path: '/settings/shop-label/:uuid',
    exact: true,
    issuerType: allIssuerAllowed,
    allowedRoles: serviceAndOrgManagerAllowed,
    layout: WithHeaderSidebar,
    component: SettingsShopLabelDetail,
  },
  {
    path: '/settings/qr-design',
    exact: true,
    issuerType: allIssuerAllowed,
    allowedRoles: organizationAllowed,
    layout: WithHeaderSidebar,
    component: QRDisplay,
  },
  {
    path: '/settings/qr-designer',
    exact: true,
    issuerType: allIssuerAllowed,
    allowedRoles: organizationAllowed,
    layout: WithHeaderSidebar,
    component: QRDesigner,
  },
  {
    path: '/service-settings/campaigns',
    exact: true,
    allowedRoles: serviceAllowed,
    layout: WithHeaderSidebar,
    component: SettingsCampaigns,
  },
  {
    path: '/service-settings/customer',
    exact: true,
    allowedRoles: serviceAllowed,
    layout: WithHeaderSidebar,
    component: SettingsCustomer,
  },
  {
    path: '/service-settings/partner-clients',
    exact: true,
    allowedRoles: serviceAllowed,
    layout: WithHeaderSidebar,
    component: PartnerClients,
  },
  {
    path: '/service-settings/partner-client/create',
    exact: true,
    allowedRoles: serviceAllowed,
    layout: WithHeaderSidebar,
    component: CreatePartnerClient,
  },
  {
    path: '/service-settings/partner-client/:id',
    exact: true,
    allowedRoles: serviceAllowed,
    layout: WithHeaderSidebar,
    component: PartnerClientDetail,
  },
  {
    path: '/service-settings/oauth-clients',
    exact: true,
    allowedRoles: serviceAllowed,
    layout: WithHeaderSidebar,
    component: OAuthClients,
  },
  {
    path: '/service-settings/oauth-client/create',
    exact: true,
    allowedRoles: serviceAllowed,
    layout: WithHeaderSidebar,
    component: CreateOAuthClient,
  },
  {
    path: '/service-settings/oauth-client/:id',
    exact: true,
    allowedRoles: serviceAllowed,
    layout: WithHeaderSidebar,
    component: OAuthClientDetail,
  },
].map((route) => {
  // /path/:id/:type => RegExp("^/path/[^/]+/[^/]+$")
  route.pathMatcher = new RegExp(`^${route.path.replace(/\:[^/]+/g, '[^/]+')}$`);
  return route;
});

export const isAllowedRoute = (route, { user, selectedPrivateMoney }) => {
  if (route.isUserAllowedFn) {
    return route.isUserAllowedFn({ user, selectedPrivateMoney });
  }
  return (
    route.allowedRoles.includes(user.role) &&
    ((route.issuerType &&
      route.issuerType.some((type) => user.organization.issuer_type.includes(type))) ||
      !route.issuerType)
  );
};

export const allowedPaths = (user) => {
  return secureRoutes.filter((route) => isAllowedRoute(route, user)).map((route) => route.path);
};

export const findPrimaryRoute = (path) => {
  return (
    secureRoutes.find((route) => route.path === path) || // find absMatch path first
    secureRoutes.find((route) => path.match(route.pathMatcher))
  ); // if no absMatch path then find regMatch path
};

export const isAllowedPath = (path, { user, selectedPrivateMoney }) => {
  const primaryRoute = findPrimaryRoute(path);

  if (primaryRoute.isPathAllowedFn) {
    return primaryRoute.isPathAllowedFn({ user, selectedPrivateMoney });
  }

  return isAllowedRoute(primaryRoute, { user, selectedPrivateMoney });
};

const Routes = () => {
  const selectedPrivateMoney = useAppSelector(selectUserPrivateMoney);

  return (
    <App>
      <Switch>
        <AppRoute exact path="/login" layout={OnePane} component={Login} />
        <AppRoute exact path="/verify" layout={OnePane} component={VerifyUser} />
        <AppRoute exact path="/reset" layout={OnePane} component={PasswordReset} />
        <Route exact path="/public/:orgCode/form/preview" component={MerchantApplicationFormPreview} />
        <Route exact path="/public/:orgCode/form/:id" component={MerchantApplicationFormApply} />
        <Redirect exact from="/settings/shops/create" to="/shops/create" />
        <Redirect exact from="/settings/shops/:id/edit" to="/shops/:id/edit" />
        <Redirect exact from="/settings/shops/:id" to="/shops/:id" />
        <Redirect exact from="/settings/shops" to="/shops" />
        {/* Backward compat: /settings/organization* was renamed to /organizations* in the 決済加盟店管理 menu reorganization */}
        <Redirect exact from="/settings/organization/create" to="/organizations/create" />
        <Redirect exact from="/settings/organization/:id" to="/organizations/:id" />
        <Redirect exact from="/settings/organizations" to="/organizations" />
        {secureRoutes.map((route, i) => {
          const isAllowed = (user) => isAllowedRoute(route, { user, selectedPrivateMoney });
          return (
            <AppRoute
              layout={Authorize(route.layout, isAllowed)}
              path={route.path}
              exact={route.exact}
              component={route.component}
              key={i}
            />
          );
        })}
        <Route component={NotFound} />
      </Switch>
    </App>
  );
};

export default hot(module)(Routes);
